import React, { useMemo } from 'react';
import { Chart } from 'src/components/HomePage/AnalyticsSection/Chart';
import { useAnalyticsAccess } from 'src/components/HomePage/AnalyticsSection/useAnalyticsAccess';
import BaseTypography from 'src/components/Text/BaseTypography';
import {
  useFetchActiveClientsQuery,
  useFetchClientsQuery,
  useFetchSubscriptionsQuery,
  AggregateResponse,
  AggregateTargets,
  getErrorMessage,
} from 'src/services/api/analyticsApi';
import { ensureApiError } from 'src/utils/Errors';
import { ChartData } from 'src/types/analytics';

const DURATION_DAYS = 30;

const BUTTON_CLASSES = [
  'appearance-none',
  'border',
  'border-solid',
  'border-gray-100',
  'text-left',
  'flex-1',
  'outline-none',
  'px-3',
  'py-2',
  'rounded',
  'transition',
  'hover:bg-gray-100',
  'hover:cursor-pointer',
  'overflow-hidden',
  'w-full',
  'md:w-auto',
].join(' ');

function getChartData(
  durationDays: number,
  data?: AggregateResponse,
): ChartData[] {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (durationDays - 1));
  const dataMap = new Map(
    data?.map((d) => [new Date(d.interval).toISOString().split('T')[0], d]) ??
      [],
  );
  let currentTotal = 0;
  return Array(durationDays)
    .fill(undefined)
    .map((_, i) => {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      const [key] = date.toISOString().split('T');
      const aggregate = dataMap.get(key);
      if (aggregate) {
        currentTotal = aggregate.total;
      }
      return {
        date,
        total: aggregate?.total ?? currentTotal,
      };
    });
}

export function ActiveState() {
  const [activeTab, setActiveTab] = React.useState<AggregateTargets>('CLIENT');
  const isVisible = useAnalyticsAccess();
  const { data: clientsData, error: clientsError } = useFetchClientsQuery(
    undefined,
    { skip: !isVisible.CLIENT },
  );
  const { data: activeClientsData, error: activeClientsError } =
    useFetchActiveClientsQuery(undefined, {
      skip: !isVisible.CLIENT_ACTIVATION,
    });
  const { data: subscriptionsData, error: subscriptionsError } =
    useFetchSubscriptionsQuery(undefined, {
      skip: !isVisible.SUBSCRIPTION,
    });

  const data = useMemo(
    () =>
      ({
        CLIENT: clientsData,
        CLIENT_ACTIVATION: activeClientsData,
        SUBSCRIPTION: subscriptionsData,
      }[activeTab]),
    [clientsData, activeClientsData, subscriptionsData, activeTab],
  );
  const error = {
    CLIENT: clientsError,
    CLIENT_ACTIVATION: activeClientsError,
    SUBSCRIPTION: subscriptionsError,
  }[activeTab];

  const chartData = useMemo(() => getChartData(DURATION_DAYS, data), [data]);

  const totals: Record<AggregateTargets, string> = useMemo(() => {
    let clientTotal = '-';
    let activeClientTotal = '-';
    let subscriptionTotal = '-';
    if (clientsData?.length) {
      const { total } = clientsData[clientsData.length - 1];
      clientTotal = `${total}` ?? '-';
    }
    if (activeClientsData?.length) {
      const { total } = activeClientsData[activeClientsData.length - 1];
      activeClientTotal = `${total}` ?? '-';
    }
    if (subscriptionsData?.length) {
      const { total } = subscriptionsData[subscriptionsData.length - 1];
      subscriptionTotal = `${total}` ?? '-';
    }
    return {
      CLIENT: clientTotal,
      CLIENT_ACTIVATION: activeClientTotal,
      SUBSCRIPTION: subscriptionTotal,
    };
  }, [clientsData, activeClientsData, subscriptionsData]);

  return (
    <>
      <header className="pb-6 flex items-center gap-3 flex-col md:flex-row">
        {(['CLIENT', 'CLIENT_ACTIVATION', 'SUBSCRIPTION'] as const).map((tab) =>
          !isVisible[tab] ? null : (
            <button
              key={tab}
              className={`${BUTTON_CLASSES} ${
                activeTab === tab ? 'bg-gray-50' : 'bg-transparent'
              }`}
              onClick={() => setActiveTab(tab)}
            >
              <BaseTypography
                className="overflow-hidden text-nowrap text-ellipsis"
                fontType="14Regular"
              >
                {
                  {
                    CLIENT: 'Clients',
                    CLIENT_ACTIVATION: 'Active clients',
                    SUBSCRIPTION: 'Subscriptions',
                  }[tab]
                }
              </BaseTypography>
              <BaseTypography fontType="15Medium">{totals[tab]}</BaseTypography>
            </button>
          ),
        )}
      </header>

      {error ? (
        <BaseTypography fontType="15Regular">
          {getErrorMessage(ensureApiError(error).message)}
        </BaseTypography>
      ) : (
        <div className="text-copilot-green">
          <Chart data={chartData} />
        </div>
      )}
    </>
  );
}
